/**
 * Turns a `SpriteDef` into a canvas of cells: one row per frame, one column per heading. Rotation
 * is done once here, in pixels, the way the arcade board's ROM held its sprites pre-turned.
 */

import { makeCanvas } from '../canvas';
import { rgb } from '../palette';
import type { SpriteDef } from './types';
import { ROTATIONS } from './types';

export interface Atlas {
  readonly canvas: HTMLCanvasElement;
  /** One cell's size; a rotating sprite's cell is a square that holds it at any angle. */
  readonly cellW: number;
  readonly cellH: number;
  readonly frames: number;
  readonly rotations: number;
  /** The art's own size. */
  readonly w: number;
  readonly h: number;
}

type Rgb = readonly [number, number, number];

function parseColour(c: number | string): Rgb {
  if (typeof c === 'number') return rgb(c);
  return [Number.parseInt(c.slice(1, 3), 16), Number.parseInt(c.slice(3, 5), 16), Number.parseInt(c.slice(5, 7), 16)];
}

/** A frame as colour numbers: 0 transparent, else 1 + index into the sprite's colour list. */
function indexFrame(frame: readonly string[], keys: readonly string[], w: number, h: number): Uint8Array {
  const out = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    const row = frame[y] ?? '';
    for (let x = 0; x < w; x++) {
      const ch = row[x] ?? '.';
      out[y * w + x] = ch === '.' ? 0 : keys.indexOf(ch) + 1;
    }
  }
  return out;
}

const SUB = [-1 / 3, 0, 1 / 3];

/**
 * The colour number under a destination pixel of a sprite turned by `angle`: nine samples vote, so
 * thin lines survive a turn and edges do not fray.
 */
function sample(src: Uint8Array, w: number, h: number, dx: number, dy: number, cos: number, sin: number, votes: Uint8Array): number {
  votes.fill(0);
  let best = 0;
  let bestVotes = 0;
  for (const sy of SUB) {
    for (const sx of SUB) {
      const px = dx + sx;
      const py = dy + sy;
      // Rotate back into the art's own space.
      const ux = Math.floor(px * cos + py * sin + w / 2);
      const uy = Math.floor(-px * sin + py * cos + h / 2);
      const v = ux < 0 || uy < 0 || ux >= w || uy >= h ? 0 : (src[uy * w + ux] ?? 0);
      const n = (votes[v] = (votes[v] ?? 0) + 1);
      // The centre sample (visited fifth) wins a tie.
      if (n > bestVotes || (n === bestVotes && sx === 0 && sy === 0)) {
        best = v;
        bestVotes = n;
      }
    }
  }
  return best;
}

export function buildAtlas(def: SpriteDef): Atlas {
  const first = def.frames[0] ?? [];
  const h = first.length;
  const w = first[0]?.length ?? 0;
  const rotations = def.rotates === true ? ROTATIONS : 1;
  const square = Math.ceil(Math.hypot(w, h) / 2) * 2;
  const cellW = rotations > 1 ? square : w;
  const cellH = rotations > 1 ? square : h;
  const keys = Object.keys(def.colours);
  const colours = keys.map((k) => parseColour(def.colours[k] ?? 0));
  const canvas = makeCanvas(cellW * rotations, cellH * def.frames.length);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  const img = ctx.createImageData(canvas.width, canvas.height);
  const votes = new Uint8Array(keys.length + 1);
  def.frames.forEach((frame, f) => {
    const src = indexFrame(frame, keys, w, h);
    for (let r = 0; r < rotations; r++) {
      const angle = (r / rotations) * Math.PI * 2;
      // Quarter turns are exact; computing them from rounded sines keeps them so.
      const cos = Math.round(Math.cos(angle) * 1e9) / 1e9;
      const sin = Math.round(Math.sin(angle) * 1e9) / 1e9;
      for (let y = 0; y < cellH; y++) {
        for (let x = 0; x < cellW; x++) {
          const v = sample(src, w, h, x + 0.5 - cellW / 2, y + 0.5 - cellH / 2, cos, sin, votes);
          if (v === 0) continue;
          const c = colours[v - 1] ?? [255, 0, 255];
          const o = ((f * cellH + y) * canvas.width + r * cellW + x) * 4;
          img.data[o] = c[0];
          img.data[o + 1] = c[1];
          img.data[o + 2] = c[2];
          img.data[o + 3] = 255;
        }
      }
    }
  });
  ctx.putImageData(img, 0, 0);
  return { canvas, cellW, cellH, frames: def.frames.length, rotations, w, h };
}

/** The column of a rotating atlas for a heading in radians (-π/2, up, is the art as drawn). */
export function rotationIndex(heading: number, rotations: number): number {
  if (rotations <= 1) return 0;
  const turn = (heading + Math.PI / 2) / (Math.PI * 2);
  return (((Math.round(turn * rotations) % rotations) + rotations) % rotations);
}
